import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { DispositionDto } from '@mark43/cad-api';
import { Text, DataList, DataItem, ButtonGroup, HStack, Icon, VStack } from '@cad-arc';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { PanelTitle } from '../../panels/panel-title';
import { useEventMetadataStore } from '../event-panel-form/event-metadata';
import { useAllEventNotifications } from '../../event-notifications/use-event-notifications';
import { observer } from 'mobx-react-lite';
import { EventNotificationsCounter } from '../tabs/components/event-notifications-counter';
import { EntityPanelHeaderResizeButtons } from '../../panels/entity-panels/entity-panel-header-resize-buttons';
import { EntityStatusIcon } from '../../entity-status-icon';
import { useIntegratedActiveDispositionsQuery } from '../event-state/use-integrated-active-disposition-query';
import { ScheduledEventKebabMenu } from './scheduled-event-kebab-menu';
import { CreateEventKebabMenu } from './create-event-kebab-menu';
import { ExternalAgencyEventKebabMenu } from './external-agency-event-kebab-menu';
import { ActiveEventKebabMenu } from './active-event-kebab-menu';
import { Tooltip } from '~/common/components/tooltip';

function createRensNode(firstRen: string, otherRens: string[]): React.ReactNode {
    if (!firstRen) return undefined;
    return (
        <>
            <Text>{firstRen}</Text>
            {otherRens.length > 0 ? (
                <Tooltip overlay={otherRens.join(', ')}>
                    <span> +{otherRens.length}</span>
                </Tooltip>
            ) : undefined}
        </>
    );
}

function DispositionInfo({
    disposition,
    additionalDispositionsEnabled,
}: {
    disposition?: DispositionDto;
    additionalDispositionsEnabled?: boolean;
}) {
    if (!disposition) {
        return null;
    }
    return (
        <Tooltip
            overlay={
                <DataList direction="column" variant="inline">
                    {additionalDispositionsEnabled && (
                        <DataItem dataKey="Additional Dispositions">
                            {disposition?.additionalDispositions.join(',')}
                        </DataItem>
                    )}
                    <DataItem dataKey={disposition.commentsTitle}>{disposition?.comments}</DataItem>
                </DataList>
            }
        >
            <Text>{disposition?.primaryDisposition ?? '-'}</Text>
        </Tooltip>
    );
}

export const EventPanelHeader = observer(function EventPanelHeader(): React.ReactElement {
    const bootstrapState = useBootstrapState();
    const { unitLevelDispositionsEnabled, additionalDispositionsEnabled } = useEnabledFeatures();
    const renLabel =
        bootstrapState.fieldDisplayNameByFieldName['AGENCY_EVENT_REPORTING_EVENT_NUMBER'];
    const eventMetadataStore = useEventMetadataStore();
    const eventId = eventMetadataStore.state.metadata?.eventId;
    const {
        isFetching: isActiveDispositionFetching,
        data: activeDispositionsDto,
    } = useIntegratedActiveDispositionsQuery({
        eventId,
        enabled: unitLevelDispositionsEnabled,
    });
    const {
        isScheduledEvent,
        isActive,
        eventClass,
        callForServiceCode,
        callTaker,
        agencyEventNumber,
        isAgencyExternal,
        parentAgencyEventId,
    } = eventMetadataStore.state.metadata ?? {};
    const eventNumberBaseDigits = agencyEventNumber?.slice(0, -4);
    const eventNumberBoldDigits = agencyEventNumber?.slice(-4);
    const [firstRen, ...otherRens] = eventMetadataStore?.eventRens ?? [];
    const allEventNotifications = useAllEventNotifications(eventId ?? -1);
    return (
        <HStack data-testid="event-panel-header" align="start" w="full" pb={1} spacing={0}>
            <VStack align="start" spacing={0} pl={2} w="full">
                <HStack justify="space-between" w="full">
                    <HStack minWidth={0}>
                        {eventMetadataStore.state.metadata ? (
                            <EventNotificationsCounter notifications={allEventNotifications} />
                        ) : undefined}
                        <PanelTitle>
                            <HStack spacing="1">
                                {agencyEventNumber ? (
                                    <>
                                        {(eventClass === 'Police' ||
                                            eventClass === 'EMS' ||
                                            eventClass === 'Fire') && (
                                            <Icon
                                                size="sm"
                                                icon={eventClass}
                                                data-testid={`icon-sm-${eventClass}`}
                                            />
                                        )}
                                        <span>{eventClass} </span>
                                        <span>
                                            #
                                            <Text as="span" fontWeight="normal">
                                                {eventNumberBaseDigits}
                                            </Text>
                                        </span>
                                        {eventNumberBoldDigits}: {callForServiceCode}
                                        {parentAgencyEventId && <Icon icon="Merge" size="sm" />}
                                    </>
                                ) : (
                                    <span>New Event</span>
                                )}
                            </HStack>
                        </PanelTitle>
                    </HStack>

                    <ButtonGroup size="sm" style={{ alignItems: 'center' }}>
                        {eventId ? (
                            <EntityStatusIcon
                                isActive={isActive}
                                isScheduledEvent={isScheduledEvent}
                            />
                        ) : undefined}
                        {isScheduledEvent ? (
                            <ScheduledEventKebabMenu />
                        ) : !eventId ? (
                            <CreateEventKebabMenu />
                        ) : isAgencyExternal ? (
                            <ExternalAgencyEventKebabMenu />
                        ) : (
                            <ActiveEventKebabMenu eventId={eventId} />
                        )}
                        <EntityPanelHeaderResizeButtons
                            isXXLEnabled={!!agencyEventNumber}
                            panelType="EVENT"
                        />
                    </ButtonGroup>
                </HStack>
                <DataList direction="row" variant="inline">
                    <DataItem dataKey="Call-Taker">{callTaker}</DataItem>
                    <DataItem dataKey={renLabel}>{createRensNode(firstRen, otherRens)}</DataItem>
                    {!isActiveDispositionFetching && !!activeDispositionsDto && (
                        <DataItem dataKey="Disposition">
                            <DispositionInfo
                                disposition={activeDispositionsDto}
                                additionalDispositionsEnabled={additionalDispositionsEnabled}
                            />
                        </DataItem>
                    )}
                </DataList>
            </VStack>
        </HStack>
    );
});
